import { create } from 'zustand'
import { Chat, Message } from '../types'
import client from '../api/client'
import { useAuthStore } from '../store/authStore'
import toast from 'react-hot-toast'
import { useTokenStore } from '../store/tokenStore'

// Кэш чатов в localStorage для мгновенного восстановления
const CHATS_CACHE_KEY = 'cached_chats'
const CURRENT_CHAT_CACHE_KEY = 'cached_current_chat'

function loadChatsCache(): Chat[] {
  try {
    const raw = localStorage.getItem(CHATS_CACHE_KEY)
    if (raw) return JSON.parse(raw)
  } catch {}
  return []
}

function saveChatsCache(chats: Chat[]) {
  try {
    if (chats.length > 0) {
      localStorage.setItem(CHATS_CACHE_KEY, JSON.stringify(chats))
    }
  } catch {}
}

function loadCurrentChatCache(): Chat | null {
  try {
    const raw = localStorage.getItem(CURRENT_CHAT_CACHE_KEY)
    if (raw) return JSON.parse(raw)
  } catch {}
  return null
}

function saveCurrentChatCache(chat: Chat | null) {
  try {
    if (chat) {
      localStorage.setItem(CURRENT_CHAT_CACHE_KEY, JSON.stringify(chat))
    } else {
      localStorage.removeItem(CURRENT_CHAT_CACHE_KEY)
    }
  } catch {}
}

interface ChatState {
  chats: Chat[]
  currentChat: Chat | null
  isLoading: boolean
  isSending: boolean
  sendError: string | null
  _abortSignal?: AbortSignal
  fetchChats: () => Promise<void>
  createChat: (model: string) => Promise<Chat>
  selectChat: (id: string) => Promise<void>
  deleteChat: (id: string) => Promise<void>
  archiveChat: (id: string) => Promise<void>
  unarchiveChat: (id: string) => Promise<void>
  sendMessage: (chatId: string, content: string, model?: string, webSearch?: boolean, files?: { name: string; base64: string; type: string }[], deepResearch?: boolean) => Promise<void>
  sendMessageStream: (chatId: string, content: string, signal: AbortSignal, model?: string, webSearch?: boolean, files?: { name: string; base64: string; type: string }[], deepResearch?: boolean) => Promise<void>
  addMessage: (message: Message) => void
  clearCurrentChat: () => void
  setSending: (sending: boolean) => void
}


const cachedChats = loadChatsCache()
const cachedCurrentChat = loadCurrentChatCache()

const useChatStoreImpl = create<ChatState>((set, get) => ({
  chats: cachedChats,
  currentChat: cachedCurrentChat,
  isLoading: false,
  isSending: false,
  sendError: null,

  fetchChats: async () => {
    set({ isLoading: true })
    try {
      const { data } = await client.get('/chat')
      console.log('[DEBUG fetchChats] got', data?.length, 'chats, tokens:', data?.map(c => c.tokensUsed).join(','))
      saveChatsCache(data)
      set({ chats: data, isLoading: false })
    } catch {
      set({ isLoading: false })
    }
  },

  createChat: async (model: string) => {
    const { data } = await client.post('/chat', { model, title: 'Новый чат' })
    set((state) => {
      const newChats = [data, ...state.chats]
      saveChatsCache(newChats)
      saveCurrentChatCache(data)
      return { chats: newChats, currentChat: data }
    })
    return data
  },

  selectChat: async (id: string) => {
    set({ isLoading: true })
    try {
      const { data } = await client.get(`/chat/${id}`)
      console.log('[DEBUG selectChat] tokensUsed:', data?.tokensUsed, 'messages tokens:', data?.messages?.map(m => ({role: m.role, tokens: m.tokens})))
      saveCurrentChatCache(data)
      set({ currentChat: data, isLoading: false })
    } catch {
      set({ isLoading: false })
    }
  },

  deleteChat: async (id: string) => {
    try {
      await client.delete(`/chat/${id}`)
      set((state) => ({
        chats: state.chats// eslint-disable-next-line @typescript-eslint/no-explicit-any
        .filter((c: any) => c.id !== id),
        currentChat: state.currentChat?.id === id ? null : state.currentChat,
      }))
    } catch (e: any) {
      console.error('deleteChat failed:', e?.response?.status, e?.response?.data)
      toast.error('Ошибка удаления: ' + (e?.response?.data?.detail || e.message))
    }
  },

  archiveChat: async (id: string) => {
    await client.post(`/chat/${id}/archive`)
    set((state) => ({
      chats: state.chats.map((c) => c.id === id ? { ...c, archived: true } : c),
    }))
  },

  unarchiveChat: async (id: string) => {
    await client.post(`/chat/${id}/unarchive`)
    set((state) => ({
      chats: state.chats.map((c) => c.id === id ? { ...c, archived: false } : c),
    }))
  },

  sendMessage: async (chatId, content, model?, webSearch?, files?) => {
    const state = get()
    if (state.isSending) return

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
    }

    set((state) => ({
      isSending: true,
      sendError: null,
      currentChat: state.currentChat
        ? {
            ...state.currentChat,
            messages: [...state.currentChat.messages, userMessage],
          }
        : null,
    }))

    try {
      const body: Record<string, any> = { content, model, webSearch }
      if (files && files.length > 0) {
        body.files = files.map((f) => f.base64)
        body.filenames = files.map((f) => f.name)
      }
      const { data } = await client.post(`/chat/${chatId}/message`, body, { signal: state._abortSignal })

      set((state) => {
        const newTitle = data.title || state.currentChat?.title || ''
        return {
          isSending: false,
          currentChat: state.currentChat
            ? {
                ...state.currentChat,
                title: newTitle,
                messages: [...state.currentChat.messages, data.message],
                tokensUsed: data.tokensUsed || state.currentChat.tokensUsed,
              }
            : null,
          chats: state.chats.map((c) =>
            c.id === chatId ? { ...c, title: newTitle } : c
          ),
        }
      })
    } catch (err: any) {
      // Если отменено пользователем — тихо выходим
      if (err?.code === 'ERR_CANCELED' || err?.message?.includes('canceled') || err?.message?.includes('abort')) {
        set({ isSending: false })
        return
      }
      const detail = err?.response?.data?.detail || 'Ошибка при получении ответа от AI'
      set({ isSending: false, sendError: detail })
    }
  },

  addMessage: (message: Message) => {
    set((state) => ({
      currentChat: state.currentChat
        ? {
            ...state.currentChat,
            messages: [...state.currentChat.messages, message],
          }
        : null,
    }))
  },

  clearCurrentChat: () => set({ currentChat: null }),

  setSending: (sending: boolean) => set({ isSending: sending }),

  sendMessageStream: async (chatId, content, signal, model?, webSearch?, files?, deepResearch?) => {
    const state = get()
    if (state.isSending) return

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
    }

    const assistantMessage: Message = {
      id: `assistant-${Date.now()}`,
      role: 'assistant',
      content: '',
      model: model || '',
      timestamp: new Date().toISOString(),
    }

    set((state) => ({
      isSending: true,
      sendError: null,
      _abortSignal: signal,
      currentChat: state.currentChat
        ? {
            ...state.currentChat,
            messages: [...state.currentChat.messages, userMessage, assistantMessage],
          }
        : null,
    }))

    try {
      const body: Record<string, any> = { content, model, webSearch, deepResearch }
      if (files && files.length > 0) {
        body.files = files.map((f) => f.base64)
        body.filenames = files.map((f) => f.name)
      }

      const token = useAuthStore.getState().token
      const response = await fetch('/api/chat/' + chatId + '/message/stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
        signal,
      })

      if (!response.ok) {
        const errData = await response.json().catch(() => ({ detail: 'Ошибка запроса' }))
        set({ isSending: false, sendError: errData.detail || `HTTP ${response.status}` })
        return
      }

      const reader = response.body?.getReader()
      if (!reader) {
        set({ isSending: false, sendError: 'Нет стрима' })
        return
      }

      const decoder = new TextDecoder()
      let buffer = ''
      let fullAssistantContent = ''
      let assistantMsgId: string | null = null
      let streamDone = false

      while (true) {
        if (streamDone) break
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        let i = 0
        while (i < lines.length) {
          const line = lines[i]
          if (line.startsWith('event: meta')) {
            // Ищем следующую data: строку
            const nextLine = lines[i + 1]
            if (nextLine?.startsWith('data: ')) {
              try {
                const meta = JSON.parse(nextLine.slice(6))
                if (meta.assistant_msg_id) {
                  assistantMsgId = meta.assistant_msg_id
                }
              } catch {}
            }
            i += 2
          } else if (line.startsWith('event: done')) {
            // Парсим data на следующей строке (может быть в той же пачке)
            let tokensUsed = 0
            let newTitle: string | null = null
            const nextLine = lines[i + 1]
            if (nextLine?.startsWith('data: ')) {
              try {
                const doneData = JSON.parse(nextLine.slice(6))
                tokensUsed = doneData.tokens_used || 0
                if (doneData.title) newTitle = doneData.title
              } catch {}
            }
            // Завершаем чтение стрима
            if (assistantMsgId) {
              set((state) => {
                if (!state.currentChat) return {}
                const msgs = [...state.currentChat.messages]
                const lastIdx = msgs.length - 1
                if (lastIdx >= 0 && msgs[lastIdx].role === 'assistant') {
                  msgs[lastIdx] = { ...msgs[lastIdx], id: assistantMsgId!, content: fullAssistantContent }
                }
                const updatedTitle = newTitle || state.currentChat.title
                return {
                  currentChat: { ...state.currentChat, messages: msgs, tokensUsed: tokensUsed, title: updatedTitle },
                  chats: state.chats.map((c) =>
                    c.id === state.currentChat!.id ? { ...c, title: updatedTitle, tokensUsed: tokensUsed } : c
                  ),
                }
              })
            }
            // Обновляем баланс
            useTokenStore.getState().fetchBalance()
            // done — конец стрима
            streamDone = true
            break
          } else if (line.startsWith('event: error')) {
            // Ошибка от бэкенда — показываем пользователю
            const nextLine = lines[i + 1]
            if (nextLine?.startsWith('data: ')) {
              try {
                const errData = JSON.parse(nextLine.slice(6))
                const errText = errData.error || errData.text || 'Ошибка'
                // Показываем ошибку как ассистент-сообщение
                set((state) => {
                  if (!state.currentChat) return {}
                  const msgs = [...state.currentChat.messages]
                  // Убираем последнее пустое ассистент-сообщение, если оно есть
                  if (msgs.length > 0 && msgs[msgs.length-1].role === 'assistant' && !msgs[msgs.length-1].content) {
                    msgs.pop()
                  }
                  msgs.push({
                    role: 'assistant',
                    content: errText,
                    id: 'error-' + Date.now(),
                    timestamp: new Date().toISOString(),
                  })
                  return { currentChat: { ...state.currentChat, messages: msgs } }
                })
              } catch {}
            }
            streamDone = true
            break
          } else if (line.startsWith('data: ')) {
            try {
              const parsed = JSON.parse(line.slice(6))
              console.log('[DEBUG SSE] data line:', line.slice(0, 80))
              if (parsed.text) {
                fullAssistantContent += parsed.text
                // Обновляем последнее сообщение (ассистента) в реальном времени
                set((state) => {
                  if (!state.currentChat) return {}
                  const msgs = [...state.currentChat.messages]
                  const lastIdx = msgs.length - 1
                  if (lastIdx >= 0 && msgs[lastIdx].role === 'assistant') {
                    msgs[lastIdx] = { ...msgs[lastIdx], content: fullAssistantContent }
                  }
                  return {
                    currentChat: {
                      ...state.currentChat,
                      messages: msgs,
                    },
                  }
                })
              }
            } catch {}
            i += 1
          } else {
            i += 1
          }
        }
      }

      set({ isSending: false })
      // Подставляем модель из переданного model (она может отличаться от s.currentChat.model
      // если пользователь переключил модель в UI)
      set((s) => {
        if (!s.currentChat) return {}
        const msgs = [...s.currentChat.messages]
        const lastIdx = msgs.length - 1
        if (lastIdx >= 0 && msgs[lastIdx].role === 'assistant') {
          msgs[lastIdx] = { ...msgs[lastIdx], model: model || s.currentChat.model || '' }
        }
        const updatedChat = { ...s.currentChat, messages: msgs }
        const updatedChats = s.chats.map((c) =>
          c.id === s.currentChat!.id
            ? { ...c, title: s.currentChat!.title, model: s.currentChat!.model }
            : c
        )
        saveCurrentChatCache(updatedChat)
        saveChatsCache(updatedChats)
        return {
          currentChat: updatedChat,
          chats: updatedChats,
        }
      })
    } catch (err: any) {
      if (err.name === 'AbortError') {
        set({ isSending: false })
        return
      }
      const detail = err?.message || 'Ошибка при стриминге'
      set({ isSending: false, sendError: detail })
    }
  },
}));

// Функция для очистки кэша при logout
export function clearChatCache() {
  localStorage.removeItem(CHATS_CACHE_KEY)
  localStorage.removeItem(CURRENT_CHAT_CACHE_KEY)
}

export const useChatStore = useChatStoreImpl

